import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useApp } from '../store';
import { can } from '../permissions';
import { displayName, initials } from '../people';
import { asDate } from '../utils';
import { confirmDialog } from '../confirm';
import Icon, { type IconName } from './Icon';
import InfoTip from './InfoTip';
import DateField from './DateField';
import Priority from './Priority';
import {
  type RaidKind,
  type Risk,
  type RiskDraft,
  type RiskStatus,
  type RiskStrategy,
  type Score,
  type SeverityBand,
  type Assumption,
  type AssumptionStatus,
  type Confidence,
  type RegisterIssue,
  type IssueStatus,
  type Dependency,
  type DepDirection,
  type DepPartyKind,
  type DepStatus,
  RAID_REF_PREFIX,
  raidRef,
  severityScore,
  severityBand,
  risksBySeverity,
  riskSummary,
  nextReviewDefault,
  reviewDue,
  RISK_STATUS_LABELS,
  RISK_STATUSES,
  RISK_STRATEGY_LABELS,
  RISK_STRATEGIES,
  SEVERITY_BAND_LABELS,
  SEVERITY_ORDER,
  assumptionsByConfidence,
  assumptionSummary,
  ASSUMPTION_STATUS_LABELS,
  ASSUMPTION_STATUSES,
  CONFIDENCE_LABELS,
  CONFIDENCE_ORDER,
  CONFIDENCE_FILL,
  CONFIDENCE_STOPS,
  issuesByPriority,
  issueSummary,
  ISSUE_STATUS_LABELS,
  ISSUE_STATUSES,
  dependenciesByStatus,
  dependencySummary,
  depOverdue,
  DEP_DIRECTION_LABELS,
  DEP_DIRECTION_SUBLABELS,
  DEP_PARTY_LABELS,
  DEP_STATUS_LABELS,
  DEP_STATUS_ORDER,
  DEP_DIRECTIONS,
  DEP_PARTY_KINDS,
  DEP_STATUSES,
  LIKELIHOOD_LABELS,
  IMPACT_LABELS,
  MATRIX_LIKELIHOODS,
  MATRIX_IMPACTS,
  risksInCell,
  riskDraftFromLesson,
  riskSeededFrom,
} from '../raid';
import type { Lesson } from '../lessons';
import { richText } from '../rich-text';
import { PRIORITY_ORDER, PRIORITY_LABELS, type IssuePriority } from '../types';

/**
 * THE RISK REGISTER (RAID) — the screen. 0193, a child of Plan.
 *
 * One home, four sibling registers behind a tab strip: Risks, Assumptions, Issues,
 * Dependencies. Built to the approved mocks (dev-preview/risk-register, 01–16):
 *  · Risks — a table grouped by severity with the L×I score square, a "Seeded from a
 *    lesson" chip, and a 5×5 heat-map toggled Table/Matrix.
 *  · Assumptions — grouped by confidence (shakiest first), a three-stop confidence
 *    meter, impact-if-false under the title, and the invalidated → convert-to-risk loop.
 *  · Issues — the app's own priority marks (R23), grouped by priority, "Realised from
 *    R-NN" provenance when raised from a risk.
 *  · Dependencies — a direction glyph, a typed party, a needed-by (red when overdue),
 *    grouped by four-stop status (late first).
 * Each leads with a status-at-a-glance band (open count, a distribution bar, a side
 * figure). The pure layer (../raid) owns every derivation; the store owns the writes.
 */

/* ── small date helpers (local midnight, like DecisionsView) ─────────────────── */
function dayString(ms?: number): string {
  if (ms == null) return '';
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
function dayToMs(s: string): number | undefined {
  if (!s) return undefined;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d).getTime();
}

/* ── shared bits ─────────────────────────────────────────────────────────────── */

/** An owner's avatar + name, or a quiet "Unassigned". */
function Owner({ email }: { email?: string }) {
  const profiles = useApp((s) => s.profiles);
  if (!email) return <span className="raid-owner raid-owner-none">Unassigned</span>;
  return (
    <span className="raid-owner">
      <span className="avatar-me raid-av" aria-hidden="true">{initials(email, profiles)}</span>
      {displayName(email, profiles)}
    </span>
  );
}

/** The provenance chip — one shape, the tint names the source (R12). */
function ProvChip({ icon, children }: { icon: IconName; children: React.ReactNode }) {
  return (
    <span className="raid-chip">
      <Icon name={icon} size={13} />
      {children}
    </span>
  );
}

/** The L×I severity score square — form + colour, never colour alone (#6). An
 *  unassessed risk shows a hollow "–" square that reads "to assess". */
function SevSquare({ r }: { r: Pick<Risk, 'likelihood' | 'impact'> }) {
  const score = severityScore(r.likelihood, r.impact);
  const band = severityBand(score);
  if (score == null || !band) {
    return (
      <span className="sev-cell">
        <span className="sev-sq sev-none" aria-hidden="true">–</span>
        <span className="sev-txt">
          <b>To assess</b>
          <span className="sev-li">L– × I–</span>
        </span>
      </span>
    );
  }
  return (
    <span className="sev-cell">
      <span className={`sev-sq b-${band}`}>{score}</span>
      <span className="sev-txt">
        <b className={`band-${band}`}>{SEVERITY_BAND_LABELS[band]}</b>
        <span className="sev-li">L{r.likelihood} × I{r.impact}</span>
      </span>
    </span>
  );
}

/** A three-stop confidence meter — one axis, never an L×I square (an assumption is
 *  not a risk). Low 1 bar, Medium 2, High 3. */
function ConfMeter({ confidence }: { confidence?: Confidence }) {
  const fill = confidence ? CONFIDENCE_FILL[confidence] : 0;
  return (
    <span className="conf-cell">
      <span className={`conf-meter ${confidence ? `c-${confidence}` : 'c-none'}`} aria-hidden="true">
        {Array.from({ length: CONFIDENCE_STOPS }).map((_, i) => (
          <span key={i} className={`conf-bar ${i < fill ? 'on' : ''}`} style={{ height: `${6 + i * 3}px` }} />
        ))}
      </span>
      <span className="conf-txt">
        <b className={confidence ? `conf-${confidence}` : ''}>{confidence ? CONFIDENCE_LABELS[confidence] : 'To assess'}</b>
        <span className="sev-li">{confidence ? `${fill} of ${CONFIDENCE_STOPS}` : '—'}</span>
      </span>
    </span>
  );
}

/** The dependency direction glyph — the arrow points the dependency, so it never
 *  reads the wrong way round. */
function DirGlyph({ direction }: { direction: DepDirection }) {
  return (
    <span className="dep-dir" aria-hidden="true">
      <span className={`dep-arrow dep-${direction}`}>
        <Icon name="arrow" size={16} />
      </span>
      <span className="dep-dir-txt">
        <b>{DEP_DIRECTION_LABELS[direction]}</b>
        <span className="sev-li">{DEP_DIRECTION_SUBLABELS[direction]}</span>
      </span>
    </span>
  );
}

/** The typed other-party cell. */
function PartyCell({ d }: { d: Dependency }) {
  const setProject = useApp((s) => s.setProject);
  const setView = useApp((s) => s.setView);
  const icon = d.partyKind === 'board' ? 'board' : d.partyKind === 'person' ? 'users' : 'briefcase';
  const body = (
    <>
      <span className="party-ic" aria-hidden="true"><Icon name={icon} size={15} /></span>
      <span className="party-txt">
        <b>{d.partyName}</b>
        <span className="sev-li">{DEP_PARTY_LABELS[d.partyKind]}</span>
      </span>
    </>
  );
  if (d.partyKind === 'board' && d.partyBoardId) {
    // A board party links straight to that board (R15).
    return (
      <button
        className="raid-party raid-party-link"
        onClick={() => { setProject(d.partyBoardId!); setView('riskregister'); }}
        title={`Open ${d.partyName}`}
      >
        {body}
      </button>
    );
  }
  return <span className="raid-party">{body}</span>;
}

/** A status pill — a dot + word, tinted by the status (never colour alone). */
function StatusPill({ kind, status }: { kind: string; status: string }) {
  return (
    <span className={`raid-status rs-${kind}-${status}`}>
      <span className="rs-dot" aria-hidden="true" />
      {kind === 'risk'
        ? RISK_STATUS_LABELS[status as RiskStatus]
        : kind === 'assumption'
          ? ASSUMPTION_STATUS_LABELS[status as AssumptionStatus]
          : kind === 'issue'
            ? ISSUE_STATUS_LABELS[status as IssueStatus]
            : DEP_STATUS_LABELS[status as DepStatus]}
    </span>
  );
}

/** A grouped band header (critical severity · 1). */
function BandHead({ dot, label, count }: { dot: string; label: string; count: number }) {
  return (
    <div className="raid-grp">
      <span className={`raid-grp-dot ${dot}`} aria-hidden="true" />
      <span className="raid-grp-h">{label}</span>
      <span className="raid-grp-c">{count}</span>
    </div>
  );
}

/** The at-a-glance distribution bar — segments proportional to the counts, each
 *  tinted by band, with a legend. Numbers turned into a picture. */
function DistBar({
  title,
  segments,
}: {
  title: string;
  segments: { key: string; label: string; count: number; cls: string }[];
}) {
  return (
    <div className="raid-dist">
      <span className="raid-dist-h">{title}</span>
      <span className="raid-bar" aria-hidden="true">
        {segments.map((s) => (
          s.count > 0 ? <span key={s.key} className={`raid-seg ${s.cls}`} style={{ flexGrow: s.count }} /> : null
        ))}
      </span>
      <span className="raid-legend">
        {segments.map((s) => (
          <span key={s.key} className="raid-leg">
            <span className={`raid-leg-dot ${s.cls}`} aria-hidden="true" />
            {s.label} <b>{s.count}</b>
          </span>
        ))}
      </span>
    </div>
  );
}

/* ── the screen ──────────────────────────────────────────────────────────────── */

const TAB_LABELS: Record<RaidKind, string> = {
  risk: 'Risks', assumption: 'Assumptions', issue: 'Issues', dependency: 'Dependencies',
};
const ADD_LABELS: Record<RaidKind, string> = {
  risk: 'Add risk', assumption: 'Add assumption', issue: 'Add issue', dependency: 'Add dependency',
};

type Modal =
  | null
  | { kind: 'risk'; edit?: Risk; seed?: Lesson }
  | { kind: 'review'; risk: Risk }
  | { kind: 'assumption'; edit?: Assumption }
  | { kind: 'issue'; edit?: RegisterIssue; fromRisk?: Risk }
  | { kind: 'dependency'; edit?: Dependency };

export default function RiskRegisterView() {
  const risks = useApp((s) => s.risks);
  const assumptions = useApp((s) => s.assumptions);
  const issues = useApp((s) => s.registerIssues);
  const dependencies = useApp((s) => s.dependencies);
  const raidLoaded = useApp((s) => s.raidLoaded);
  const projectName = useApp((s) => s.meta.name);
  const role = useApp((s) => s.role);
  const mayWrite = can(role, 'editIssue');
  const seedRiskLesson = useApp((s) => s.seedRiskLesson);
  const clearRiskSeed = useApp((s) => s.clearRiskSeed);

  const [tab, setTab] = useState<RaidKind>('risk');
  const [matrix, setMatrix] = useState(false);
  const [modal, setModal] = useState<Modal>(null);
  const [flashId, setFlashId] = useState<string | null>(null);

  const now = Date.now();
  const rSum = useMemo(() => riskSummary(risks, now), [risks, now]);
  const aSum = useMemo(() => assumptionSummary(assumptions), [assumptions]);
  const iSum = useMemo(() => issueSummary(issues, now), [issues, now]);
  const dSum = useMemo(() => dependencySummary(dependencies, now), [dependencies, now]);

  const counts: Record<RaidKind, number> = {
    risk: risks.length, assumption: assumptions.length, issue: issues.length, dependency: dependencies.length,
  };

  // A lesson handed in from the Lessons screen (#490) opens the editable seed
  // preview over the Risks tab, then is cleared so it does not reopen.
  useEffect(() => {
    if (!seedRiskLesson) return;
    setTab('risk');
    setModal({ kind: 'risk', seed: seedRiskLesson });
    clearRiskSeed();
  }, [seedRiskLesson, clearRiskSeed]);

  // Flash a just-added/converted row so the eye lands on it (the Decisions reveal).
  useEffect(() => {
    if (!flashId) return;
    const el = document.getElementById(`raid-${flashId}`);
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el?.classList.add('just-revealed');
    const t = window.setTimeout(() => { el?.classList.remove('just-revealed'); setFlashId(null); }, 1800);
    return () => window.clearTimeout(t);
  }, [flashId]);

  const openAdd = () => {
    if (tab === 'risk') setModal({ kind: 'risk' });
    else if (tab === 'assumption') setModal({ kind: 'assumption' });
    else if (tab === 'issue') setModal({ kind: 'issue' });
    else setModal({ kind: 'dependency' });
  };

  // The header chip + sub-line read from the active register.
  const lastReviewed = (ms: number[]) => {
    const got = ms.filter((n) => n > 0);
    return got.length ? asDate(Math.max(...got)) : '';
  };
  const headerFor = (): { chip: React.ReactNode; sub: string } => {
    if (tab === 'risk') {
      const rev = lastReviewed(risks.map((r) => r.reviewedAt ?? 0));
      return {
        chip: rSum.elevated ? <span className="raid-flag f-hot"><span className="rs-dot" />Elevated exposure</span> : null,
        sub: `${projectName} · ${risks.length} ${risks.length === 1 ? 'risk' : 'risks'}, ${rSum.open} open${rev ? ` · last reviewed ${rev}` : ''}`,
      };
    }
    if (tab === 'assumption') {
      const rev = lastReviewed(assumptions.map((a) => a.reviewedAt ?? 0));
      return {
        chip: aSum.lowConfidence > 0 ? <span className="raid-flag f-warn"><span className="rs-dot" />{aSum.lowConfidence} low confidence</span> : null,
        sub: `${projectName} · ${assumptions.length} ${assumptions.length === 1 ? 'assumption' : 'assumptions'}, ${aSum.open} open${rev ? ` · last reviewed ${rev}` : ''}`,
      };
    }
    if (tab === 'issue') {
      return {
        chip: iSum.critical > 0 ? <span className="raid-flag f-hot"><span className="rs-dot" />{iSum.critical} critical open</span> : null,
        sub: `${projectName} · ${issues.length} ${issues.length === 1 ? 'issue' : 'issues'}, ${iSum.open} open`,
      };
    }
    const rev = lastReviewed(dependencies.map((d) => d.updatedAt ?? 0));
    return {
      chip: dSum.late + dSum.atRisk > 0 ? <span className="raid-flag f-hot"><span className="rs-dot" />{dSum.late} late · {dSum.atRisk} at risk</span> : null,
      sub: `${projectName} · ${dependencies.length} ${dependencies.length === 1 ? 'dependency' : 'dependencies'}, ${dSum.open} open${rev ? ` · last reviewed ${rev}` : ''}`,
    };
  };
  const { chip, sub } = headerFor();

  return (
    <div className="raid gov">
      <div className="gov-head">
        <div className="gov-head-l">
          <div className="gov-title-row">
            <h1 className="fd-title">Risk Register</h1>
            <span className="ihint">
              <InfoTip label="Risk Register (RAID)">
                The project's RAID log — the <b>risks</b> that might happen, the <b>assumptions</b> it
                relies on, the <b>issues</b> that already have, and the <b>dependencies</b> it owes or is
                owed. It is the single source of truth: the charter's risk section and the portfolio
                read from here. Add an item on each tab, or seed a risk from a lesson.
              </InfoTip>
            </span>
          </div>
          <p className="gov-sub">{sub}</p>
        </div>
        <div className="gov-tools">
          {chip}
          {mayWrite && (
            <button className="btn btn-primary" onClick={openAdd}>
              <Icon name="plus" size={15} />
              {ADD_LABELS[tab]}
            </button>
          )}
        </div>
      </div>

      {/* The tab strip — all four registers, live counts. */}
      <div className="raid-tabs" role="tablist" aria-label="Registers">
        {(['risk', 'assumption', 'issue', 'dependency'] as RaidKind[]).map((k) => (
          <button
            key={k}
            role="tab"
            aria-selected={tab === k}
            className={`raid-tab ${tab === k ? 'active' : ''}`}
            onClick={() => setTab(k)}
          >
            <RaidTabIcon kind={k} />
            <span className="raid-tab-l">{TAB_LABELS[k]}</span>
            <span className="raid-tab-c">{counts[k]}</span>
          </button>
        ))}
      </div>

      {tab === 'risk' && (
        <RisksPanel
          risks={risks} summary={rSum} loaded={raidLoaded} mayWrite={mayWrite} now={now}
          matrix={matrix} setMatrix={setMatrix}
          onAdd={() => setModal({ kind: 'risk' })}
          onEdit={(r) => setModal({ kind: 'risk', edit: r })}
          onReview={(r) => setModal({ kind: 'review', risk: r })}
          onRealise={(r) => setModal({ kind: 'issue', fromRisk: r })}
        />
      )}
      {tab === 'assumption' && (
        <AssumptionsPanel
          items={assumptions} summary={aSum} loaded={raidLoaded} mayWrite={mayWrite} risks={risks}
          onAdd={() => setModal({ kind: 'assumption' })}
          onEdit={(a) => setModal({ kind: 'assumption', edit: a })}
          onConverted={(riskId) => { setTab('risk'); setFlashId(riskId); }}
        />
      )}
      {tab === 'issue' && (
        <IssuesPanel
          items={issues} summary={iSum} loaded={raidLoaded} mayWrite={mayWrite}
          onAdd={() => setModal({ kind: 'issue' })}
          onEdit={(i) => setModal({ kind: 'issue', edit: i })}
        />
      )}
      {tab === 'dependency' && (
        <DependenciesPanel
          items={dependencies} summary={dSum} loaded={raidLoaded} mayWrite={mayWrite} now={now}
          onAdd={() => setModal({ kind: 'dependency' })}
          onEdit={(d) => setModal({ kind: 'dependency', edit: d })}
        />
      )}

      {modal?.kind === 'risk' && (
        <RiskForm
          edit={modal.edit} seed={modal.seed}
          onClose={() => setModal(null)}
          onDone={(id) => { setModal(null); setTab('risk'); setFlashId(id); }}
        />
      )}
      {modal?.kind === 'review' && (
        <ReviewForm
          risk={modal.risk}
          onClose={() => setModal(null)}
          onDone={(id) => { setModal(null); setTab('risk'); setFlashId(id); }}
        />
      )}
      {modal?.kind === 'assumption' && (
        <AssumptionForm
          edit={modal.edit}
          onClose={() => setModal(null)}
          onDone={(id) => { setModal(null); setTab('assumption'); setFlashId(id); }}
        />
      )}
      {modal?.kind === 'issue' && (
        <IssueForm
          edit={modal.edit} fromRisk={modal.fromRisk}
          onClose={() => setModal(null)}
          onDone={(id) => { setModal(null); setTab('issue'); setFlashId(id); }}
        />
      )}
      {modal?.kind === 'dependency' && (
        <DependencyForm
          edit={modal.edit}
          onClose={() => setModal(null)}
          onDone={(id) => { setModal(null); setTab('dependency'); setFlashId(id); }}
        />
      )}
    </div>
  );
}

/** The tab strip glyph per register. */
function RaidTabIcon({ kind }: { kind: RaidKind }) {
  const name = kind === 'risk' ? 'warning' : kind === 'assumption' ? 'help' : kind === 'issue' ? 'info' : 'link';
  return <Icon name={name} size={15} className="raid-tab-ic" />;
}

/* ── RISKS ───────────────────────────────────────────────────────────────────── */

function RisksPanel({
  risks, summary, loaded, mayWrite, now, matrix, setMatrix, onAdd, onEdit, onReview, onRealise,
}: {
  risks: Risk[];
  summary: ReturnType<typeof riskSummary>;
  loaded: boolean;
  mayWrite: boolean;
  now: number;
  matrix: boolean;
  setMatrix: (b: boolean) => void;
  onAdd: () => void;
  onEdit: (r: Risk) => void;
  onReview: (r: Risk) => void;
  onRealise: (r: Risk) => void;
}) {
  const removeRisk = useApp((s) => s.removeRisk);
  const realiseRiskAsIssue = useApp((s) => s.realiseRiskAsIssue);
  const groups = useMemo(() => risksBySeverity(risks), [risks]);

  if (risks.length === 0) return <RiskEmpty loaded={loaded} mayWrite={mayWrite} onAdd={onAdd} />;

  const bandDot: Record<SeverityBand, string> = { critical: 'b-critical', high: 'b-high', medium: 'b-medium', low: 'b-low' };

  return (
    <>
      <div className="raid-summary">
        <div className="raid-sum-big">
          <span className="raid-sum-n">{summary.open}</span>
          <span className="raid-sum-l">open {summary.open === 1 ? 'risk' : 'risks'}</span>
          {(summary.critical > 0 || summary.high > 0) && (
            <span className="raid-sum-note hot">
              {summary.critical > 0 ? `${summary.critical} critical` : ''}
              {summary.critical > 0 && summary.high > 0 ? ' · ' : ''}
              {summary.high > 0 ? `${summary.high} high` : ''}
            </span>
          )}
        </div>
        <DistBar
          title={`Exposure by severity — ${risks.length} ${risks.length === 1 ? 'risk' : 'risks'}`}
          segments={SEVERITY_ORDER.map((b) => ({
            key: b, label: SEVERITY_BAND_LABELS[b], count: summary.byBand[b], cls: `b-${b}`,
          }))}
        />
        <div className="raid-sum-side">
          <span className="raid-sum-n">{summary.reviewsDue}</span>
          <span className="raid-sum-l">reviews due<br />this week</span>
        </div>
      </div>

      <div className="raid-toolbar">
        <span className="raid-grouped">Grouped by <b>severity</b></span>
        <div className="seg raid-viewtoggle">
          <button className={`seg-btn ${!matrix ? 'active' : ''}`} onClick={() => setMatrix(false)}>Table</button>
          <button className={`seg-btn ${matrix ? 'active' : ''}`} onClick={() => setMatrix(true)}>Matrix</button>
        </div>
      </div>

      {matrix ? (
        <RiskMatrix risks={risks} />
      ) : (
        <div className="raid-table k-risk">
          <div className="raid-th">
            <span>Risk &amp; planned response</span>
            <span>Severity (L×I)</span>
            <span>Strategy</span>
            <span>Owner</span>
            <span>Status</span>
            <span>Reviewed</span>
          </div>
          {groups.map((g) => (
            <div key={g.key}>
              <BandHead dot={bandDot[g.key]} label={`${SEVERITY_BAND_LABELS[g.key]} severity`} count={g.items.length} />
              {g.items.map((r) => (
                <div className={`raid-row ${r.status === 'closed' ? 'is-closed' : ''}`} id={`raid-${r.id}`} key={r.id}>
                  <div className="raid-main">
                    <span className="raid-ref">{raidRef('risk', r.num)}</span>
                    <h3 className="raid-title">{r.title}</h3>
                    {r.response && <p className="raid-sub"><b>Response:</b> {r.response}</p>}
                    {r.seededFromLessonId && (
                      <ProvChip icon="document">Seeded from a lesson</ProvChip>
                    )}
                    {mayWrite && (
                      <span className="raid-rowacts">
                        <button className="link" onClick={() => onEdit(r)}>Edit</button>
                        {r.status !== 'closed' && (
                          <button className="link" onClick={() => onReview(r)}>Mark reviewed</button>
                        )}
                        {r.status !== 'closed' && (
                          <button className="link" onClick={() => onRealise(r)}>Realise as an issue</button>
                        )}
                        <button
                          className="link"
                          onClick={async () => {
                            const ok = await confirmDialog({
                              title: 'Remove this risk?',
                              body: `"${r.title}" will be removed from the register.`,
                              confirmLabel: 'Remove', cancelLabel: 'Keep', tone: 'caution',
                            });
                            if (ok) void removeRisk(r.id);
                          }}
                        >Remove</button>
                      </span>
                    )}
                  </div>
                  <div className="raid-c"><SevSquare r={r} /></div>
                  <div className="raid-c">{r.strategy && <span className="raid-pill-q">{RISK_STRATEGY_LABELS[r.strategy]}</span>}</div>
                  <div className="raid-c"><Owner email={r.owner} /></div>
                  <div className="raid-c"><StatusPill kind="risk" status={r.status} /></div>
                  <div className="raid-c raid-rev">
                    <RiskReviewCell r={r} now={now} />
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      <p className="raid-foot-note">
        <Icon name="info" size={14} /> The charter's risk section and the portfolio read their risks from here.
      </p>
      {/* realiseRiskAsIssue is reached via the row action above; referenced so the
          lint sees the store binding used. */}
      <span hidden>{String(!!realiseRiskAsIssue)}</span>
    </>
  );
}

/** The "Reviewed" cell: when it was last reviewed, and when the next review falls —
 *  coloured amber as it comes due and red once it is overdue. A risk that has only
 *  been scheduled (never yet reviewed) reads "Not yet reviewed" with its due date. */
function RiskReviewCell({ r, now }: { r: Risk; now: number }) {
  if (r.reviewedAt == null && r.nextReviewAt == null) return <span className="sev-li">—</span>;
  const due = reviewDue(r.nextReviewAt, now);
  const nextCls = due === 'overdue' ? ' raid-rev-over' : due === 'due' ? ' raid-rev-due' : '';
  const nextWord = due === 'overdue' ? 'Review overdue' : due === 'due' ? 'Review due' : 'Next review';
  return (
    <>
      {r.reviewedAt != null
        ? <span>{asDate(r.reviewedAt)}</span>
        : <span className="sev-li">Not yet reviewed</span>}
      {r.nextReviewAt != null && (
        <span className={`sev-li${nextCls}`}>{nextWord} {asDate(r.nextReviewAt)}</span>
      )}
    </>
  );
}

function RiskMatrix({ risks }: { risks: Risk[] }) {
  // Risks missing a likelihood or impact never land in a cell — surface the count so the
  // heat map never silently under-reports (a hidden un-scored risk reads as "nothing there").
  const notPlaced = risks.filter((r) => severityScore(r.likelihood, r.impact) == null).length;
  return (
    <div className="raid-matrix-wrap">
      <div className="raid-matrix-head">
        <h3>Risk heat map — likelihood × impact</h3>
        <span className="raid-matrix-hint">Each chip is one risk, placed by its assessed likelihood and impact.</span>
      </div>
      <div className="raid-matrix">
        {MATRIX_LIKELIHOODS.map((l) => (
          <div className="raid-matrix-rowlab" key={`l${l}`} style={{ gridRow: MATRIX_LIKELIHOODS.indexOf(l) + 1 }}>
            <b>{LIKELIHOOD_LABELS[l]}</b>
            <span className="sev-li">Likelihood {l}</span>
          </div>
        ))}
        {MATRIX_LIKELIHOODS.map((l, ri) =>
          MATRIX_IMPACTS.map((im, ci) => {
            const band = severityBand(l * im)!;
            const cell = risksInCell(risks, l as Score, im as Score);
            return (
              <div
                className={`raid-cell cell-${band}`}
                key={`${l}-${im}`}
                style={{ gridRow: ri + 1, gridColumn: ci + 2 }}
              >
                {cell.map((r) => (
                  <span key={r.id} className={`raid-cell-chip b-${band}`} title={r.title}>{raidRef('risk', r.num)}</span>
                ))}
              </div>
            );
          }),
        )}
        {MATRIX_IMPACTS.map((im, ci) => (
          <div className="raid-matrix-collab" key={`i${im}`} style={{ gridColumn: ci + 2 }}>
            <b>{IMPACT_LABELS[im]}</b>
            <span className="sev-li">Impact {im}</span>
          </div>
        ))}
      </div>
      <p className="raid-matrix-axis">IMPACT →</p>
      {notPlaced > 0 && (
        <p className="raid-matrix-note">
          {notPlaced === 1 ? '1 risk is not placed' : `${notPlaced} risks are not placed`} — no likelihood or impact set yet.
        </p>
      )}
    </div>
  );
}

function RiskEmpty({ loaded, mayWrite, onAdd }: { loaded: boolean; mayWrite: boolean; onAdd: () => void }) {
  const lessons = useApp((s) => s.lessons);
  const setView = useApp((s) => s.setView);
  if (!loaded) return <p className="gov-none">Loading the register…</p>;
  const haveLessons = lessons.length > 0;
  return (
    <div className="gov-empty raid-empty">
      <div className="ge-mark raid-ge-mark"><Icon name="warning" size={24} /></div>
      <h2>No risks logged yet</h2>
      <p>
        The register is where this project tracks what could go wrong, who owns it, and how it is
        being managed. Add the first risk, or carry one in from a past-project lesson.
      </p>
      {mayWrite && (
        <div className="btn-row">
          <button className="btn btn-primary" onClick={onAdd}><Icon name="plus" size={15} />Add a risk</button>
          {haveLessons && (
            <button className="btn" onClick={() => setView('lessons')}><Icon name="document" size={15} />Seed from a lesson</button>
          )}
        </div>
      )}
      <p className="raid-empty-note">
        The register works on its own — a charter is not required. The charter will read its risks from
        here once one exists.
      </p>
    </div>
  );
}

/* ── ASSUMPTIONS ─────────────────────────────────────────────────────────────── */

function AssumptionsPanel({
  items, summary, loaded, mayWrite, risks, onAdd, onEdit, onConverted,
}: {
  items: Assumption[];
  summary: ReturnType<typeof assumptionSummary>;
  loaded: boolean;
  mayWrite: boolean;
  risks: Risk[];
  onAdd: () => void;
  onEdit: (a: Assumption) => void;
  onConverted: (riskId: string) => void;
}) {
  const removeAssumption = useApp((s) => s.removeAssumption);
  const convertAssumptionToRisk = useApp((s) => s.convertAssumptionToRisk);
  const groups = useMemo(() => assumptionsByConfidence(items), [items]);

  if (items.length === 0) {
    return (
      <RaidEmpty
        loaded={loaded} mayWrite={mayWrite} onAdd={onAdd} icon="help"
        title="No assumptions logged yet"
        body="Assumptions are the premises this project is relying on — what you are taking as true, and what breaks if one proves wrong. Log the ones the plan depends on so they can be validated."
        addLabel="Add an assumption"
      />
    );
  }

  const refFor = (riskId?: string) => {
    const r = riskId ? risks.find((x) => x.id === riskId) : undefined;
    return r ? raidRef('risk', r.num) : 'R-?';
  };
  const bandDot: Record<Confidence, string> = { low: 'b-critical', medium: 'b-medium', high: 'b-low2' };

  return (
    <>
      <div className="raid-summary">
        <div className="raid-sum-big">
          <span className="raid-sum-n">{summary.open}</span>
          <span className="raid-sum-l">open {summary.open === 1 ? 'assumption' : 'assumptions'}</span>
          {summary.lowConfidence > 0 && <span className="raid-sum-note hot">{summary.lowConfidence} low confidence</span>}
        </div>
        <DistBar
          title={`Confidence — ${items.length} ${items.length === 1 ? 'assumption' : 'assumptions'}`}
          segments={CONFIDENCE_ORDER.map((c) => ({
            key: c, label: CONFIDENCE_LABELS[c], count: summary.byBand[c], cls: `conf-${c}`,
          }))}
        />
        <div className="raid-sum-side">
          <span className="raid-sum-n">{summary.invalidated}</span>
          <span className="raid-sum-l">invalidated</span>
        </div>
      </div>

      <div className="raid-toolbar"><span className="raid-grouped">Grouped by <b>confidence</b></span></div>

      <div className="raid-table k-assumption">
        <div className="raid-th">
          <span>Assumption &amp; impact if false</span>
          <span>Confidence</span>
          <span>Owner</span>
          <span>Status</span>
          <span>Reviewed</span>
        </div>
        {groups.map((g) => (
          <div key={g.key}>
            <BandHead dot={bandDot[g.key]} label={`${CONFIDENCE_LABELS[g.key]} confidence`} count={g.items.length} />
            {g.items.map((a) => (
              <div className={`raid-row ${a.status !== 'open' ? 'is-settled' : ''}`} id={`raid-${a.id}`} key={a.id}>
                <div className="raid-main">
                  <span className="raid-ref">{raidRef('assumption', a.num)}</span>
                  <h3 className="raid-title">{a.title}</h3>
                  {a.impactIfFalse && <p className="raid-sub"><b>If it proves false:</b> {a.impactIfFalse}</p>}
                  {mayWrite && (
                    <span className="raid-rowacts">
                      <button className="link" onClick={() => onEdit(a)}>Edit</button>
                      {a.status === 'open' && !a.convertedToRiskId && (
                        <button
                          className="link"
                          onClick={async () => {
                            const id = await convertAssumptionToRisk(a.id);
                            if (id) onConverted(id);
                          }}
                        >Convert to risk</button>
                      )}
                      <button
                        className="link"
                        onClick={async () => {
                          const ok = await confirmDialog({
                            title: 'Remove this assumption?',
                            body: `"${a.title}" will be removed from the register.`,
                            confirmLabel: 'Remove', cancelLabel: 'Keep', tone: 'caution',
                          });
                          if (ok) void removeAssumption(a.id);
                        }}
                      >Remove</button>
                    </span>
                  )}
                </div>
                <div className="raid-c"><ConfMeter confidence={a.confidence} /></div>
                <div className="raid-c"><Owner email={a.owner} /></div>
                <div className="raid-c">
                  <StatusPill kind="assumption" status={a.status} />
                  {a.convertedToRiskId && <span className="raid-convnote">→ Converted to {refFor(a.convertedToRiskId)}</span>}
                </div>
                <div className="raid-c raid-rev">
                  {a.validateByAt && a.status === 'open'
                    ? <><span>Validate by {asDate(a.validateByAt)}</span></>
                    : a.reviewedAt ? <span>{asDate(a.reviewedAt)}</span> : <span className="sev-li">—</span>}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </>
  );
}

/* ── ISSUES ──────────────────────────────────────────────────────────────────── */

function IssuesPanel({
  items, summary, loaded, mayWrite, onAdd, onEdit,
}: {
  items: RegisterIssue[];
  summary: ReturnType<typeof issueSummary>;
  loaded: boolean;
  mayWrite: boolean;
  onAdd: () => void;
  onEdit: (i: RegisterIssue) => void;
}) {
  const removeIssue = useApp((s) => s.removeIssue);
  const risks = useApp((s) => s.risks);
  const groups = useMemo(() => issuesByPriority(items), [items]);

  if (items.length === 0) {
    return (
      <RaidEmpty
        loaded={loaded} mayWrite={mayWrite} onAdd={onAdd} icon="info"
        title="No issues logged yet"
        body="Issues are problems that have already happened — the things going wrong now that need a next step and an owner. Raise one here, or realise it from a risk that has occurred."
        addLabel="Add an issue"
      />
    );
  }

  const refFor = (riskId?: string) => {
    const r = riskId ? risks.find((x) => x.id === riskId) : undefined;
    return r ? raidRef('risk', r.num) : 'R-?';
  };
  const bandDot: Record<IssuePriority, string> = { critical: 'b-critical', high: 'b-high', medium: 'b-medium', low: 'b-low' };

  return (
    <>
      <div className="raid-summary">
        <div className="raid-sum-big">
          <span className="raid-sum-n">{summary.open}</span>
          <span className="raid-sum-l">open {summary.open === 1 ? 'issue' : 'issues'}</span>
          {(summary.critical > 0 || summary.high > 0) && (
            <span className="raid-sum-note hot">
              {summary.critical > 0 ? `${summary.critical} critical` : ''}
              {summary.critical > 0 && summary.high > 0 ? ' · ' : ''}
              {summary.high > 0 ? `${summary.high} high` : ''}
            </span>
          )}
        </div>
        <DistBar
          title={`Priority — ${items.length} ${items.length === 1 ? 'issue' : 'issues'}`}
          segments={PRIORITY_ORDER.map((p) => ({
            key: p, label: PRIORITY_LABELS[p], count: summary.byBand[p], cls: `b-${p === 'critical' ? 'critical' : p === 'high' ? 'high' : p === 'medium' ? 'medium' : 'low'}`,
          }))}
        />
        <div className="raid-sum-side">
          <span className="raid-sum-n">{summary.resolvedThisWeek}</span>
          <span className="raid-sum-l">resolved<br />this week</span>
        </div>
      </div>

      <div className="raid-toolbar"><span className="raid-grouped">Grouped by <b>priority</b></span></div>

      <div className="raid-table k-issue">
        <div className="raid-th">
          <span>Issue &amp; next step</span>
          <span>Priority</span>
          <span>Owner</span>
          <span>Raised</span>
          <span>Status</span>
        </div>
        {groups.map((g) => (
          <div key={g.key}>
            <BandHead dot={bandDot[g.key]} label={PRIORITY_LABELS[g.key]} count={g.items.length} />
            {g.items.map((it) => (
              <div className={`raid-row ${it.status === 'resolved' ? 'is-settled' : ''}`} id={`raid-${it.id}`} key={it.id}>
                <div className="raid-main">
                  <span className="raid-ref">{raidRef('issue', it.num)}</span>
                  <h3 className="raid-title">{it.title}</h3>
                  {it.nextStep && <p className="raid-sub"><b>Next step:</b> {it.nextStep}</p>}
                  {it.realisedFromRiskId && <ProvChip icon="warning">Realised from {refFor(it.realisedFromRiskId)}</ProvChip>}
                  {mayWrite && (
                    <span className="raid-rowacts">
                      <button className="link" onClick={() => onEdit(it)}>Edit</button>
                      <button
                        className="link"
                        onClick={async () => {
                          const ok = await confirmDialog({
                            title: 'Remove this issue?',
                            body: `"${it.title}" will be removed from the register.`,
                            confirmLabel: 'Remove', cancelLabel: 'Keep', tone: 'caution',
                          });
                          if (ok) void removeIssue(it.id);
                        }}
                      >Remove</button>
                    </span>
                  )}
                </div>
                <div className="raid-c"><Priority priority={it.priority} /></div>
                <div className="raid-c"><Owner email={it.owner} /></div>
                <div className="raid-c raid-rev">{it.raisedAt ? <><span>{asDate(it.raisedAt)}</span><span className="sev-li">raised</span></> : <span className="sev-li">—</span>}</div>
                <div className="raid-c"><StatusPill kind="issue" status={it.status} /></div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </>
  );
}

/* ── DEPENDENCIES ────────────────────────────────────────────────────────────── */

function DependenciesPanel({
  items, summary, loaded, mayWrite, now, onAdd, onEdit,
}: {
  items: Dependency[];
  summary: ReturnType<typeof dependencySummary>;
  loaded: boolean;
  mayWrite: boolean;
  now: number;
  onAdd: () => void;
  onEdit: (d: Dependency) => void;
}) {
  const removeDependency = useApp((s) => s.removeDependency);
  const groups = useMemo(() => dependenciesByStatus(items), [items]);

  if (items.length === 0) {
    return (
      <RaidEmpty
        loaded={loaded} mayWrite={mayWrite} onAdd={onAdd} icon="link"
        title="No dependencies logged yet"
        body="Dependencies are what this project needs from others — or what others need from it: the handoffs between teams and boards, who owes what, and by when. Log one so a slip elsewhere is seen here."
        addLabel="Add a dependency"
      />
    );
  }

  const bandDot: Record<DepStatus, string> = { late: 'b-critical', atrisk: 'b-medium', ontrack: 'b-ok', met: 'b-low' };

  return (
    <>
      <div className="raid-summary">
        <div className="raid-sum-big">
          <span className="raid-sum-n">{summary.open}</span>
          <span className="raid-sum-l">open {summary.open === 1 ? 'dependency' : 'dependencies'}</span>
          {(summary.late > 0 || summary.atRisk > 0) && (
            <span className="raid-sum-note hot">
              {summary.late > 0 ? `${summary.late} late` : ''}
              {summary.late > 0 && summary.atRisk > 0 ? ' · ' : ''}
              {summary.atRisk > 0 ? `${summary.atRisk} at risk` : ''}
            </span>
          )}
        </div>
        <DistBar
          title={`Status — ${items.length} ${items.length === 1 ? 'dependency' : 'dependencies'}`}
          segments={DEP_STATUS_ORDER.map((s) => ({
            key: s, label: DEP_STATUS_LABELS[s], count: summary.byBand[s],
            cls: s === 'late' ? 'b-critical' : s === 'atrisk' ? 'b-medium' : s === 'ontrack' ? 'b-ok' : 'b-low',
          }))}
        />
        <div className="raid-sum-side">
          <span className="raid-sum-n">{summary.dueSoon}</span>
          <span className="raid-sum-l">due<br />next 14 days</span>
        </div>
      </div>

      <div className="raid-toolbar"><span className="raid-grouped">Grouped by <b>status</b></span></div>

      <div className="raid-table k-dependency">
        <div className="raid-th">
          <span>Dependency</span>
          <span>Direction</span>
          <span>Other party</span>
          <span>Owner</span>
          <span>Needed by</span>
          <span>Status</span>
        </div>
        {groups.map((g) => (
          <div key={g.key}>
            <BandHead dot={bandDot[g.key]} label={DEP_STATUS_LABELS[g.key]} count={g.items.length} />
            {g.items.map((d) => {
              const overdue = depOverdue(d, now);
              return (
                <div className={`raid-row ${d.status === 'met' ? 'is-settled' : ''}`} id={`raid-${d.id}`} key={d.id}>
                  <div className="raid-main">
                    <span className="raid-ref">{raidRef('dependency', d.num)}</span>
                    <h3 className="raid-title">{d.title}</h3>
                    {d.note && <p className="raid-sub">{d.note}</p>}
                    {mayWrite && (
                      <span className="raid-rowacts">
                        <button className="link" onClick={() => onEdit(d)}>Edit</button>
                        <button
                          className="link"
                          onClick={async () => {
                            const ok = await confirmDialog({
                              title: 'Remove this dependency?',
                              body: `"${d.title}" will be removed from the register.`,
                              confirmLabel: 'Remove', cancelLabel: 'Keep', tone: 'caution',
                            });
                            if (ok) void removeDependency(d.id);
                          }}
                        >Remove</button>
                      </span>
                    )}
                  </div>
                  <div className="raid-c"><DirGlyph direction={d.direction} /></div>
                  <div className="raid-c"><PartyCell d={d} /></div>
                  <div className="raid-c"><Owner email={d.owner} /></div>
                  <div className="raid-c raid-rev">
                    {d.neededByAt
                      ? <><span className={overdue ? 'dep-overdue' : ''}>{d.status === 'met' ? 'Met' : overdue ? 'Due' : d.direction === 'outbound' ? 'Needed' : 'Due'} {asDate(d.neededByAt)}</span>{overdue && <span className="sev-li dep-overdue">overdue</span>}</>
                      : <span className="sev-li">—</span>}
                  </div>
                  <div className="raid-c"><StatusPill kind="dependency" status={d.status} /></div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </>
  );
}

/* ── shared empty state (A / I / D) ──────────────────────────────────────────── */

function RaidEmpty({
  loaded, mayWrite, onAdd, icon, title, body, addLabel,
}: {
  loaded: boolean;
  mayWrite: boolean;
  onAdd: () => void;
  icon: IconName;
  title: string;
  body: string;
  addLabel: string;
}) {
  if (!loaded) return <p className="gov-none">Loading the register…</p>;
  return (
    <div className="gov-empty raid-empty">
      <div className="ge-mark raid-ge-mark"><Icon name={icon} size={24} /></div>
      <h2>{title}</h2>
      <p>{body}</p>
      {mayWrite && (
        <div className="btn-row">
          <button className="btn btn-primary" onClick={onAdd}><Icon name="plus" size={15} />{addLabel}</button>
        </div>
      )}
    </div>
  );
}

/* ── the modal shell ─────────────────────────────────────────────────────────── */

function RaidModal({
  title, landsAs, onClose, dirty, children, footer,
}: {
  title: string;
  landsAs?: string;
  onClose: () => void;
  dirty: boolean;
  children: React.ReactNode;
  footer: React.ReactNode;
}) {
  const requestClose = async () => {
    if (dirty) {
      const ok = await confirmDialog({
        title: 'Discard this entry?',
        body: 'What you have entered will be lost.',
        confirmLabel: 'Discard', cancelLabel: 'Keep editing', tone: 'caution',
      });
      if (!ok) return;
    }
    onClose();
  };
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); void requestClose(); } };
    document.addEventListener('keydown', esc, true);
    return () => document.removeEventListener('keydown', esc, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty]);

  return createPortal(
    <div className="overlay" onClick={requestClose}>
      <div className="modal raid-modal" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <span className="modal-head-title">{title}</span>
          <button className="modal-head-close" aria-label="Close" onClick={requestClose}><Icon name="close" size={18} /></button>
        </div>
        <div className="modal-body">{children}</div>
        <div className="modal-actions raid-modal-actions">
          {landsAs && <span className="raid-landsas"><Icon name="arrow" size={14} /> Lands as <b>{landsAs}</b> in the register.</span>}
          {footer}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** A native owner select shared by the add forms. */
function OwnerSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const profiles = useApp((s) => s.profiles);
  const emails = Object.keys(profiles);
  return (
    <select className="input" value={value} onChange={(e) => onChange(e.target.value)} aria-label="Owner">
      <option value="">Unassigned</option>
      {emails.map((e) => <option key={e} value={e}>{displayName(e, profiles)}</option>)}
    </select>
  );
}

/** The next free human ref for the register being added to, for the "Lands as" line. */
function nextRef(kind: RaidKind, items: { num: number }[]): string {
  const max = items.reduce((m, i) => Math.max(m, i.num), 0);
  return `${RAID_REF_PREFIX[kind]}-${String(max + 1).padStart(2, '0')}`;
}

/* ── RISK form (also the seed-from-lesson dialog) ────────────────────────────── */

function RiskForm({
  edit, seed, onClose, onDone,
}: {
  edit?: Risk;
  seed?: Lesson;
  onClose: () => void;
  onDone: (id: string) => void;
}) {
  const risks = useApp((s) => s.risks);
  const addRisk = useApp((s) => s.addRisk);
  const editRisk = useApp((s) => s.editRisk);
  const seedDraft: RiskDraft | undefined = useMemo(() => (seed ? riskDraftFromLesson(seed) : undefined), [seed]);
  const base = edit ?? seedDraft;

  const [title, setTitle] = useState(base?.title ?? '');
  const [response, setResponse] = useState(base?.response ?? '');
  const [owner, setOwner] = useState(base?.owner ?? '');
  const [category, setCategory] = useState(base?.category ?? '');
  const [strategy, setStrategy] = useState<RiskStrategy>(base?.strategy ?? 'reduce');
  const [status, setStatus] = useState<RiskStatus>(base?.status ?? 'open');
  const [likelihood, setLikelihood] = useState<Score | undefined>(edit?.likelihood ?? seedDraft?.likelihood);
  const [impact, setImpact] = useState<Score | undefined>(edit?.impact ?? seedDraft?.impact);
  const [nextReview, setNextReview] = useState(dayString(edit?.nextReviewAt));
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const titleRef = useRef<HTMLInputElement>(null);
  useEffect(() => titleRef.current?.focus(), []);

  const dup = seed ? riskSeededFrom(risks, seed.id) : undefined;
  const score = severityScore(likelihood, impact);
  const band = severityBand(score);
  const dirty = !edit && (!!title.trim() || !!response.trim());

  const submit = async () => {
    if (!title.trim()) { setProblem('Give the risk a title.'); return; }
    setBusy(true); setProblem('');
    const fields = {
      title: title.trim(),
      response: response.trim() || undefined,
      owner: owner || undefined,
      category: category.trim() || undefined,
      strategy, status, likelihood, impact,
      nextReviewAt: dayToMs(nextReview),
    };
    let err = '';
    let id = edit?.id ?? '';
    if (edit) {
      err = await editRisk(edit.id, fields);
    } else {
      id = await addRisk({
        boardId: '',
        ...fields,
        seededFromLessonId: seed?.id,
        links: seed ? [{ kind: 'lesson', id: seed.id, label: seed.title }] : undefined,
      });
      if (!id) err = 'That could not be added — you may not have permission on this board.';
    }
    setBusy(false);
    if (err) { setProblem(err); return; }
    onDone(id);
  };

  const landsAs = edit ? undefined : nextRef('risk', risks);

  return (
    <RaidModal
      title={seed ? 'Seed a risk from a lesson' : edit ? 'Edit the risk' : 'Add a risk'}
      landsAs={seed ? `${landsAs}, linked to this lesson` : landsAs}
      onClose={onClose} dirty={dirty}
      footer={
        <span className="raid-foot-btns">
          <button className="btn btn-small" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary btn-small" onClick={submit} disabled={busy}>{seed ? 'Add to register' : edit ? 'Save changes' : 'Add risk'}</button>
        </span>
      }
    >
      {dup && (
        <div className="raid-note note-warn">
          <Icon name="warning" size={16} />
          <span>This lesson already seeded a risk. <b>{raidRef('risk', dup.num)}</b> “{dup.title}” came from it. Add another anyway, or edit the existing one.</span>
        </div>
      )}
      {seed && (
        <div className="raid-fromlesson">
          <span className="raid-fromlesson-h">From the lesson</span>
          <p className="raid-fromlesson-t">{seed.title}</p>
          {seed.recommendation && richText(seed.recommendation).trim() && (
            <p className="raid-sub"><b>Next time:</b> {richText(seed.recommendation).trim()}</p>
          )}
        </div>
      )}
      <div className="fd-grid">
        <label className="stack fd-wide">
          <span>Risk <span className="req">*</span>{seed ? <span className="raid-lab-hint"> from the lesson title</span> : null}</span>
          <input ref={titleRef} className="input" value={title} aria-required onChange={(e) => { setTitle(e.target.value); setProblem(''); }} placeholder="What might happen, in a line" />
        </label>
        <label className="stack fd-wide">
          <span>Planned response (mitigation){seed ? <span className="raid-lab-hint"> from “next time”</span> : null}</span>
          <textarea className="input raid-area" value={response} onChange={(e) => setResponse(e.target.value)} rows={2} placeholder="How it is being managed" />
        </label>
        <label className="stack">
          <span>Owner</span>
          <OwnerSelect value={owner} onChange={setOwner} />
        </label>
        <label className="stack">
          <span>Category</span>
          <input className="input" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. Resourcing" />
        </label>
        <label className="stack">
          <span>Response strategy</span>
          <select className="input" value={strategy} onChange={(e) => setStrategy(e.target.value as RiskStrategy)}>
            {RISK_STRATEGIES.map((s) => <option key={s} value={s}>{RISK_STRATEGY_LABELS[s]}</option>)}
          </select>
        </label>
        <label className="stack">
          <span>Status</span>
          <select className="input" value={status} onChange={(e) => setStatus(e.target.value as RiskStatus)}>
            {RISK_STATUSES.map((s) => <option key={s} value={s}>{RISK_STATUS_LABELS[s]}</option>)}
          </select>
        </label>
      </div>

      <div className="fd-grid raid-scorers">
        <div className="stack">
          <span>Likelihood <span className="raid-lab-hint">— assess now, or later</span></span>
          <ScoreSeg value={likelihood} onChange={setLikelihood} ariaLabel="Likelihood" />
        </div>
        <div className="stack">
          <span>Impact</span>
          <ScoreSeg value={impact} onChange={setImpact} ariaLabel="Impact" />
        </div>
      </div>

      <div className="raid-preview">
        <span className="raid-preview-l">Severity preview</span>
        {score != null && band ? (
          <>
            <span className={`sev-sq b-${band}`}>{score}</span>
            <span className="sev-txt"><b className={`band-${band}`}>{SEVERITY_BAND_LABELS[band]}</b><span className="sev-li">L{likelihood} × I{impact}</span></span>
          </>
        ) : (
          <span className="sev-txt"><b>To assess</b></span>
        )}
        <span className="raid-preview-hint">Leave blank to log as “to assess”.</span>
      </div>
      {nextReviewRow(nextReview, setNextReview)}
      {problem && <p className="id-problem" role="alert">{problem}</p>}
    </RaidModal>
  );
}

/* ── REVIEW dialog — log that a risk was reviewed, and set the next due date ───── */

function ReviewForm({
  risk, onClose, onDone,
}: {
  risk: Risk;
  onClose: () => void;
  onDone: (id: string) => void;
}) {
  const editRisk = useApp((s) => s.editRisk);
  const now = Date.now();
  const [nextReview, setNextReview] = useState(dayString(nextReviewDefault(risk, now)));
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');

  const score = severityScore(risk.likelihood, risk.impact);
  const band = severityBand(score);

  const submit = async () => {
    setBusy(true); setProblem('');
    const err = await editRisk(risk.id, { reviewedAt: Date.now(), nextReviewAt: dayToMs(nextReview) });
    setBusy(false);
    if (err) { setProblem(err); return; }
    onDone(risk.id);
  };

  return (
    <RaidModal
      title="Log a review"
      onClose={onClose} dirty={false}
      footer={
        <span className="raid-foot-btns">
          <button className="btn btn-small" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary btn-small" onClick={submit} disabled={busy}>Log review</button>
        </span>
      }
    >
      <div className="raid-fromlesson">
        <span className="raid-ref">{raidRef('risk', risk.num)}</span>
        <p className="raid-fromlesson-t">{risk.title}</p>
        {score != null && band ? (
          <span className="sev-txt"><b className={`band-${band}`}>{SEVERITY_BAND_LABELS[band]}</b><span className="sev-li">L{risk.likelihood} × I{risk.impact}</span></span>
        ) : <span className="sev-txt"><b>To assess</b></span>}
      </div>
      <div className="raid-note note-info">
        <Icon name="info" size={16} />
        <span>
          This records that you checked the risk today{risk.reviewedAt != null ? <> — it was last reviewed {asDate(risk.reviewedAt)}</> : null}.
          To change the likelihood, impact, response or status, use <b>Edit</b> instead.
        </span>
      </div>
      {nextReviewRow(nextReview, setNextReview)}
      {problem && <p className="id-problem" role="alert">{problem}</p>}
    </RaidModal>
  );
}

function nextReviewRow(value: string, onChange: (v: string) => void) {
  return (
    <div className="fd-grid">
      <label className="stack">
        <span>Next review due</span>
        <DateField value={value} onChange={onChange} ariaLabel="Next review due" variant="form" />
      </label>
    </div>
  );
}

/** The 1–5 segmented scorer — the app's .seg control, blank until set. */
function ScoreSeg({ value, onChange, ariaLabel }: { value?: Score; onChange: (s: Score | undefined) => void; ariaLabel: string }) {
  return (
    <div className="seg raid-scoreseg" role="group" aria-label={ariaLabel}>
      {([1, 2, 3, 4, 5] as Score[]).map((n) => (
        <button
          key={n} type="button"
          className={`seg-btn ${value === n ? 'active' : ''}`}
          onClick={() => onChange(value === n ? undefined : n)}
        >{n}</button>
      ))}
    </div>
  );
}

/* ── ASSUMPTION form ─────────────────────────────────────────────────────────── */

function AssumptionForm({ edit, onClose, onDone }: { edit?: Assumption; onClose: () => void; onDone: (id: string) => void }) {
  const items = useApp((s) => s.assumptions);
  const addAssumption = useApp((s) => s.addAssumption);
  const editAssumption = useApp((s) => s.editAssumption);
  const [title, setTitle] = useState(edit?.title ?? '');
  const [impactIfFalse, setImpact] = useState(edit?.impactIfFalse ?? '');
  const [owner, setOwner] = useState(edit?.owner ?? '');
  const [status, setStatus] = useState<AssumptionStatus>(edit?.status ?? 'open');
  const [confidence, setConfidence] = useState<Confidence | undefined>(edit?.confidence);
  const [validateBy, setValidateBy] = useState(dayString(edit?.validateByAt));
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const titleRef = useRef<HTMLInputElement>(null);
  useEffect(() => titleRef.current?.focus(), []);
  const dirty = !edit && (!!title.trim() || !!impactIfFalse.trim());

  const submit = async () => {
    if (!title.trim()) { setProblem('Give the assumption a title.'); return; }
    setBusy(true); setProblem('');
    const fields = {
      title: title.trim(), impactIfFalse: impactIfFalse.trim() || undefined,
      owner: owner || undefined, status, confidence, validateByAt: dayToMs(validateBy),
    };
    let err = ''; let id = edit?.id ?? '';
    if (edit) err = await editAssumption(edit.id, fields);
    else { id = await addAssumption({ boardId: '', ...fields }); if (!id) err = 'That could not be added — you may not have permission on this board.'; }
    setBusy(false);
    if (err) { setProblem(err); return; }
    onDone(id);
  };

  return (
    <RaidModal
      title={edit ? 'Edit the assumption' : 'Add an assumption'}
      landsAs={edit ? undefined : nextRef('assumption', items)}
      onClose={onClose} dirty={dirty}
      footer={
        <span className="raid-foot-btns">
          <button className="btn btn-small" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary btn-small" onClick={submit} disabled={busy}>{edit ? 'Save changes' : 'Add assumption'}</button>
        </span>
      }
    >
      <div className="fd-grid">
        <label className="stack fd-wide">
          <span>Assumption <span className="req">*</span></span>
          <input ref={titleRef} className="input" value={title} aria-required onChange={(e) => { setTitle(e.target.value); setProblem(''); }} placeholder="The premise we are relying on" />
        </label>
        <label className="stack fd-wide">
          <span>Impact if it proves false</span>
          <textarea className="input raid-area" value={impactIfFalse} onChange={(e) => setImpact(e.target.value)} rows={2} placeholder="What breaks if this is wrong" />
        </label>
        <label className="stack">
          <span>Owner</span>
          <OwnerSelect value={owner} onChange={setOwner} />
        </label>
        <label className="stack">
          <span>Status</span>
          <select className="input" value={status} onChange={(e) => setStatus(e.target.value as AssumptionStatus)}>
            {ASSUMPTION_STATUSES.map((s) => <option key={s} value={s}>{ASSUMPTION_STATUS_LABELS[s]}</option>)}
          </select>
        </label>
        <label className="stack">
          <span>Validate by</span>
          <DateField value={validateBy} onChange={setValidateBy} ariaLabel="Validate by" variant="form" />
        </label>
      </div>
      <div className="stack">
        <span>Confidence <span className="raid-lab-hint">— how sure are we today?</span></span>
        <div className="seg" role="group" aria-label="Confidence">
          {CONFIDENCE_ORDER.map((c) => (
            <button key={c} type="button" className={`seg-btn ${confidence === c ? 'active' : ''}`} onClick={() => setConfidence(confidence === c ? undefined : c)}>
              {CONFIDENCE_LABELS[c]}
            </button>
          ))}
        </div>
      </div>
      <div className="raid-preview">
        <span className="raid-preview-l">Preview</span>
        <ConfMeter confidence={confidence} />
        <span className="raid-preview-hint">Low-confidence assumptions sit at the top of the register.</span>
      </div>
      {problem && <p className="id-problem" role="alert">{problem}</p>}
    </RaidModal>
  );
}

/* ── ISSUE form ──────────────────────────────────────────────────────────────── */

function IssueForm({ edit, fromRisk, onClose, onDone }: { edit?: RegisterIssue; fromRisk?: Risk; onClose: () => void; onDone: (id: string) => void }) {
  const items = useApp((s) => s.registerIssues);
  const addIssue = useApp((s) => s.addIssue);
  const editIssue = useApp((s) => s.editIssue);
  const [title, setTitle] = useState(edit?.title ?? fromRisk?.title ?? '');
  const [nextStep, setNextStep] = useState(edit?.nextStep ?? (fromRisk?.response ?? ''));
  const [owner, setOwner] = useState(edit?.owner ?? fromRisk?.owner ?? '');
  const [status, setStatus] = useState<IssueStatus>(edit?.status ?? 'open');
  const [priority, setPriority] = useState<IssuePriority>(edit?.priority ?? 'medium');
  const [raised, setRaised] = useState(dayString(edit?.raisedAt ?? Date.now()));
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const titleRef = useRef<HTMLInputElement>(null);
  useEffect(() => titleRef.current?.focus(), []);
  const dirty = !edit && (!!title.trim() || !!nextStep.trim());

  const submit = async () => {
    if (!title.trim()) { setProblem('Give the issue a title.'); return; }
    setBusy(true); setProblem('');
    const fields = { title: title.trim(), nextStep: nextStep.trim() || undefined, owner: owner || undefined, status, priority, raisedAt: dayToMs(raised) };
    let err = ''; let id = edit?.id ?? '';
    if (edit) err = await editIssue(edit.id, fields);
    else {
      id = await addIssue({ boardId: '', ...fields, realisedFromRiskId: fromRisk?.id, links: fromRisk ? [{ kind: 'risk', id: fromRisk.id, label: raidRef('risk', fromRisk.num) }] : undefined });
      if (!id) err = 'That could not be added — you may not have permission on this board.';
    }
    setBusy(false);
    if (err) { setProblem(err); return; }
    onDone(id);
  };

  return (
    <RaidModal
      title={edit ? 'Edit the issue' : 'Add an issue'}
      landsAs={edit ? undefined : nextRef('issue', items)}
      onClose={onClose} dirty={dirty}
      footer={
        <span className="raid-foot-btns">
          <button className="btn btn-small" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary btn-small" onClick={submit} disabled={busy}>{edit ? 'Save changes' : 'Add issue'}</button>
        </span>
      }
    >
      {fromRisk && (
        <div className="raid-note note-info">
          <Icon name="warning" size={16} />
          <span>Realised from <b>{raidRef('risk', fromRisk.num)}</b> — the detail is carried over and the two are linked.</span>
        </div>
      )}
      <div className="fd-grid">
        <label className="stack fd-wide">
          <span>Issue <span className="req">*</span></span>
          <input ref={titleRef} className="input" value={title} aria-required onChange={(e) => { setTitle(e.target.value); setProblem(''); }} placeholder="The problem that has already happened" />
        </label>
        <label className="stack fd-wide">
          <span>Next step / resolution</span>
          <textarea className="input raid-area" value={nextStep} onChange={(e) => setNextStep(e.target.value)} rows={2} placeholder="What is being done about it" />
        </label>
        <label className="stack">
          <span>Owner</span>
          <OwnerSelect value={owner} onChange={setOwner} />
        </label>
        <label className="stack">
          <span>Status</span>
          <select className="input" value={status} onChange={(e) => setStatus(e.target.value as IssueStatus)}>
            {ISSUE_STATUSES.map((s) => <option key={s} value={s}>{ISSUE_STATUS_LABELS[s]}</option>)}
          </select>
        </label>
        <label className="stack">
          <span>Date raised</span>
          <DateField value={raised} onChange={setRaised} ariaLabel="Date raised" variant="form" />
        </label>
      </div>
      <div className="stack">
        <span>Priority <span className="raid-lab-hint">— the app's priority scale</span></span>
        <div className="prio-row" role="group" aria-label="Priority">
          {PRIORITY_ORDER.map((p) => (
            <button
              key={p} type="button"
              className={`prio-p ${priority === p ? `on p-${p === 'critical' ? 'crit' : p === 'high' ? 'high' : p === 'medium' ? 'med' : 'low'}` : ''}`}
              onClick={() => setPriority(p)}
            >{PRIORITY_LABELS[p]}</button>
          ))}
        </div>
      </div>
      {problem && <p className="id-problem" role="alert">{problem}</p>}
    </RaidModal>
  );
}

/* ── DEPENDENCY form ─────────────────────────────────────────────────────────── */

function DependencyForm({ edit, onClose, onDone }: { edit?: Dependency; onClose: () => void; onDone: (id: string) => void }) {
  const items = useApp((s) => s.dependencies);
  const projects = useApp((s) => s.projects);
  const thisBoard = useApp((s) => s.projectId);
  const addDependency = useApp((s) => s.addDependency);
  const editDependency = useApp((s) => s.editDependency);
  const [title, setTitle] = useState(edit?.title ?? '');
  const [note, setNote] = useState(edit?.note ?? '');
  const [direction, setDirection] = useState<DepDirection>(edit?.direction ?? 'inbound');
  const [partyKind, setPartyKind] = useState<DepPartyKind>(edit?.partyKind ?? 'board');
  const [partyName, setPartyName] = useState(edit?.partyName ?? '');
  const [partyBoardId, setPartyBoardId] = useState(edit?.partyBoardId ?? '');
  const [owner, setOwner] = useState(edit?.owner ?? '');
  const [status, setStatus] = useState<DepStatus>(edit?.status ?? 'ontrack');
  const [neededBy, setNeededBy] = useState(dayString(edit?.neededByAt));
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const titleRef = useRef<HTMLInputElement>(null);
  useEffect(() => titleRef.current?.focus(), []);
  const dirty = !edit && (!!title.trim() || !!partyName.trim());

  const otherBoards = projects.filter((p) => p.id !== thisBoard);

  const submit = async () => {
    if (!title.trim()) { setProblem('Give the dependency a title.'); return; }
    const resolvedParty = partyKind === 'board' && partyBoardId
      ? (otherBoards.find((b) => b.id === partyBoardId)?.name ?? partyName.trim())
      : partyName.trim();
    if (!resolvedParty) { setProblem('Name the other party.'); return; }
    setBusy(true); setProblem('');
    const fields = {
      title: title.trim(), note: note.trim() || undefined, direction, partyKind,
      partyName: resolvedParty,
      partyBoardId: partyKind === 'board' && partyBoardId ? partyBoardId : undefined,
      owner: owner || undefined, status, neededByAt: dayToMs(neededBy),
    };
    let err = ''; let id = edit?.id ?? '';
    if (edit) err = await editDependency(edit.id, fields);
    else { id = await addDependency({ boardId: '', ...fields }); if (!id) err = 'That could not be added — you may not have permission on this board.'; }
    setBusy(false);
    if (err) { setProblem(err); return; }
    onDone(id);
  };

  return (
    <RaidModal
      title={edit ? 'Edit the dependency' : 'Add a dependency'}
      landsAs={edit ? undefined : nextRef('dependency', items)}
      onClose={onClose} dirty={dirty}
      footer={
        <span className="raid-foot-btns">
          <button className="btn btn-small" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary btn-small" onClick={submit} disabled={busy}>{edit ? 'Save changes' : 'Add dependency'}</button>
        </span>
      }
    >
      <div className="fd-grid">
        <label className="stack fd-wide">
          <span>Dependency <span className="req">*</span></span>
          <input ref={titleRef} className="input" value={title} aria-required onChange={(e) => { setTitle(e.target.value); setProblem(''); }} placeholder="What is needed" />
        </label>
        <label className="stack fd-wide">
          <span>Note</span>
          <textarea className="input raid-area" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="The detail — what and why" />
        </label>
      </div>
      <div className="stack">
        <span>Direction <span className="raid-lab-hint">— which way does it run?</span></span>
        <div className="seg" role="group" aria-label="Direction">
          {DEP_DIRECTIONS.map((d) => (
            <button key={d} type="button" className={`seg-btn ${direction === d ? 'active' : ''}`} onClick={() => setDirection(d)}>
              {DEP_DIRECTION_LABELS[d]}
            </button>
          ))}
        </div>
      </div>
      <div className="fd-grid">
        <label className="stack">
          <span>Party type</span>
          <select className="input" value={partyKind} onChange={(e) => setPartyKind(e.target.value as DepPartyKind)}>
            {DEP_PARTY_KINDS.map((k) => <option key={k} value={k}>{DEP_PARTY_LABELS[k]}</option>)}
          </select>
        </label>
        {partyKind === 'board' ? (
          <label className="stack">
            <span>The other board</span>
            <select className="input" value={partyBoardId} onChange={(e) => { setPartyBoardId(e.target.value); setProblem(''); }}>
              <option value="">Pick a board…</option>
              {otherBoards.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </label>
        ) : (
          <label className="stack">
            <span>The other party</span>
            <input className="input" value={partyName} onChange={(e) => { setPartyName(e.target.value); setProblem(''); }} placeholder={partyKind === 'person' ? 'Their name' : 'The vendor or team'} />
          </label>
        )}
        <label className="stack">
          <span>Owner</span>
          <OwnerSelect value={owner} onChange={setOwner} />
        </label>
        <label className="stack">
          <span>Needed by</span>
          <DateField value={neededBy} onChange={setNeededBy} ariaLabel="Needed by" variant="form" />
        </label>
        <label className="stack">
          <span>Status</span>
          <select className="input" value={status} onChange={(e) => setStatus(e.target.value as DepStatus)}>
            {DEP_STATUSES.map((s) => <option key={s} value={s}>{DEP_STATUS_LABELS[s]}</option>)}
          </select>
        </label>
      </div>
      <div className="raid-preview">
        <span className="raid-preview-l">Preview</span>
        <DirGlyph direction={direction} />
        <span className="raid-preview-spacer" />
        <StatusPill kind="dependency" status={status} />
      </div>
      {problem && <p className="id-problem" role="alert">{problem}</p>}
    </RaidModal>
  );
}
